/**
 * Site health — SEO & AEO checklist (AB#418 / AB#1034).
 *
 * The API owns the wording and ranking: render `status.sentence`, the
 * `next_steps` (at most 3) and `rows` as-is.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

export type SiteHealthTone = 'good' | 'attention' | 'setup';

export type SiteHealthAction =
  | { kind: 'link'; label: string; /** Admin route, e.g. `/sites/{slug}/search`. */ to: string }
  | { kind: 'fix'; label: string; /** Pass to `siteHealthApi.fix`. */ fix: SiteHealthFix };

/** One-click fixes the API can apply. */
export type SiteHealthFix = 'use_domain_as_site_url';

export interface SiteHealthRow {
  id: string;
  ok: boolean;
  /** Plain language — safe to show as the row label. */
  title: string;
  /** Tooltip text (may use SEO terms). */
  hint: string | null;
  /** Higher = more important. */
  impact: number;
  source: 'core' | 'dataforseo';
  /** Not measurable yet: not counted in passed/total, never a next step. */
  pending: boolean;
  /** Information only (e.g. Google first-page count, paid-source rows): not counted, never a next step. */
  info: boolean;
  action: SiteHealthAction | null;
}

/** DataForSEO rows (AB#1035) are merged into `rows`; this says whether they can be. */
export interface SiteHealthDataForSeo {
  configured: boolean;
  /** Last successful DataForSEO fetch (ISO 8601). */
  fetched_at: string | null;
  /** Plain sentence, e.g. "Your DataForSEO key was rejected"; no DataForSEO rows then. */
  error: string | null;
}

export interface SiteHealth {
  status: { tone: SiteHealthTone; sentence: string; passed: number; total: number };
  next_steps: SiteHealthRow[];
  rows: SiteHealthRow[];
  dataforseo: SiteHealthDataForSeo;
}

export const siteHealthApi = {
  get: (siteSlug: string) => api.get<ApiResponse<SiteHealth>>(`/v1/sites/${siteSlug}/health`),

  /** Applies the fix and returns the refreshed health payload. */
  fix: (siteSlug: string, fix: SiteHealthFix) =>
    api.post<ApiResponse<SiteHealth>>(`/v1/sites/${siteSlug}/health/fix/${fix}`, {}),
};
